import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { AppGlobals } from '@app/app.global';

export interface ChildPermissionMap {
  [menuKey: string]: {
    menuAccess: boolean;
    read: boolean;
    write: boolean;
    update: boolean;
    delete: boolean;
    custom?: { [key: string]: boolean };
  };
}

const STORAGE_KEY = 'kharchaChildPermissions';

@Injectable({ providedIn: 'root' })
export class ChildPermissionService {
  private _permissions$ = new BehaviorSubject<ChildPermissionMap | null>(this.loadFromStorage());

  constructor(private http: HttpClient, private appGlobals: AppGlobals) {}

  /** Synchronously load permissions from localStorage on app init */
  private loadFromStorage(): ChildPermissionMap | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('[ChildPermissionService] Restored permissions from localStorage:', parsed);
        return parsed;
      }
    } catch (e) {
      // ignore
    }
    return null;
  }

  get permissions(): ChildPermissionMap | null {
    return this._permissions$.value;
  }

  get permissions$(): Observable<ChildPermissionMap | null> {
    return this._permissions$.asObservable();
  }

  /** Load permissions from API and cache. Call on login for child lead admins. */
  loadPermissions(): Observable<any> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}lead-admin/my-permissions`).pipe(
      tap(res => {
        console.log('[ChildPermissionService] API Response:', res);
        const rawPerms = res?.data || {};
        // Convert snake_case keys to camelCase (both outer menu keys and inner field keys)
        const camelPerms: ChildPermissionMap = {};
        for (const menuKey of Object.keys(rawPerms)) {
          const camelKey = this.snakeToCamel(menuKey);
          const rawFields = rawPerms[menuKey];
          camelPerms[camelKey] = {
            menuAccess: rawFields.menu_access ?? rawFields.menuAccess ?? false,
            read:       rawFields.read ?? false,
            write:      rawFields.write ?? false,
            update:     rawFields.update ?? false,
            delete:     rawFields.delete ?? false,
            custom:     Object.keys(rawFields.custom || {}).reduce((acc, k) => {
                          acc[this.snakeToCamel(k)] = rawFields.custom[k];
                          return acc;
                        }, {} as { [key: string]: boolean })
          };
        }
        console.log('[ChildPermissionService] Saving camelCase permissions:', camelPerms);
        // Persist to localStorage so sidebar gets correct data on next page reload
        localStorage.setItem(STORAGE_KEY, JSON.stringify(camelPerms));
        this._permissions$.next(camelPerms);
      }),
      catchError(err => {
        console.error('[ChildPermissionService] Failed to load permissions:', err);
        // Don't overwrite with {} on error — keep existing cached permissions if any
        if (!this._permissions$.value) {
          this._permissions$.next({});
        }
        return of(null);
      })
    );
  }

  /** Convert snake_case string to camelCase */
  private snakeToCamel(str: string): string {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  /** Check if a menu is accessible by its camelCase key (e.g. 'hyperpureReports') */
  canAccessMenu(menuKeyCamel: string): boolean {
    const perms = this._permissions$.value;
    if (perms === null) {
      console.log(`[ChildPermissionService] canAccessMenu('${menuKeyCamel}') -> true (perms is null, not loaded or not child)`);
      return true; // Not a child lead admin — allow all
    }
    const hasAccess = perms[menuKeyCamel]?.menuAccess === true;
    console.log(`[ChildPermissionService] canAccessMenu('${menuKeyCamel}') -> ${hasAccess} (perms[${menuKeyCamel}] = ${JSON.stringify(perms[menuKeyCamel])})`);
    return hasAccess;
  }

  /** Check a specific permission action (read/write/update/delete/custom) */
  canDo(menuKeyCamel: string, action: string, isCustom = false): boolean {
    const perms = this._permissions$.value;
    if (perms === null) return true;
    if (isCustom) {
      return perms[menuKeyCamel]?.custom?.[this.toCamelCase(action)] === true;
    }
    return perms[menuKeyCamel]?.[action as 'read' | 'write' | 'update' | 'delete'] === true;
  }

  /** Convert snake_case key to camelCase */
  toCamelCase(str: string): string {
    return str.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
  }

  /** Clear permissions on logout */
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this._permissions$.next(null);
  }
}
